import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  FormControlLabel,
  Switch,
  Chip,
  Autocomplete,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import ContactService from '../../services/contact.service';
import ContactGroupService from '../../services/contact-group.service';

const ContactForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [groups, setGroups] = useState<any[]>([]);
  const [formData, setFormData] = useState<any>({
    firstName: '',
    phone: '',
    isActive: true,
    source: 'manual',
    tags: [],
  });

  useEffect(() => {
    fetchGroups();
    if (isEdit && id) {
      fetchContact(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchGroups = async () => {
    try {
      const response = await ContactGroupService.getAll({ populate: '*' });
      setGroups(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchContact = async (contactId: number) => {
    try {
      const response = await ContactService.getOne(contactId);
      const contact = response.data?.data?.attributes;
      if (contact) {
        setFormData({
          ...contact,
          contactGroups: contact.contactGroups?.data?.map((g: any) => g.id) || [],
        });
      }
    } catch (error) {
      console.error('Error fetching contact:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: e.target.type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isEdit && id) {
        await ContactService.update(parseInt(id), formData);
      } else {
        await ContactService.create(formData);
      }
      navigate('/contacts');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save contact');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Contact' : 'New Contact'}
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Typography variant="h6" color="primary">
              Personal Information
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                required
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName || ''}
                onChange={handleChange}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                required
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="WhatsApp Number"
                name="whatsappNumber"
                value={formData.whatsappNumber || ''}
                onChange={handleChange}
                helperText="Include country code (e.g., +1234567890)"
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email || ''}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Instagram Handle"
                name="instagramHandle"
                value={formData.instagramHandle || ''}
                onChange={handleChange}
                placeholder="@username"
              />
            </Stack>

            <Typography variant="h6" color="primary" mt={2}>
              Company Information
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Company"
                name="company"
                value={formData.company || ''}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Position"
                name="position"
                value={formData.position || ''}
                onChange={handleChange}
              />
            </Stack>

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Address"
              name="address"
              value={formData.address || ''}
              onChange={handleChange}
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={formData.city || ''}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Country"
                name="country"
                value={formData.country || ''}
                onChange={handleChange}
              />
            </Stack>

            <Typography variant="h6" color="primary" mt={2}>
              Additional Information
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Birthday"
                name="birthday"
                type="date"
                value={formData.birthday || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                select
                label="Source"
                name="source"
                value={formData.source || 'manual'}
                onChange={handleChange}
              >
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="import">Import</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="instagram">Instagram</MenuItem>
                <MenuItem value="website">Website</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Stack>

            <Autocomplete
              multiple
              options={groups.map((g) => ({ id: g.id, label: g.attributes?.name }))}
              value={
                groups
                  .filter((g) => formData.contactGroups?.includes(g.id))
                  .map((g) => ({ id: g.id, label: g.attributes?.name })) || []
              }
              onChange={(_, newValue) =>
                setFormData((prev: any) => ({
                  ...prev,
                  contactGroups: newValue.map((v) => v.id),
                }))
              }
              renderInput={(params) => (
                <TextField {...params} label="Contact Groups" placeholder="Select groups" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option.label} {...getTagProps({ index })} />
                ))
              }
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              name="notes"
              value={formData.notes || ''}
              onChange={handleChange}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive || false}
                  onChange={handleChange}
                  name="isActive"
                />
              }
              label="Active Contact"
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/contacts')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default ContactForm;

